// @flow

import * as React from 'react';
import classNames from 'classnames';
import { createRefetchContainer, graphql } from 'react-relay/compat';

import BuildTooltip from './BuildTooltip';
import BuildStates from 'app/constants/BuildStates';
import AnchoredPopover from 'app/components/shared/Popover/anchored';
import { BAR_HEIGHT_MINIMUM, BAR_WIDTH, BAR_WIDTH_WITH_SEPERATOR, GRAPH_HEIGHT } from './constants';
import type { Bar_build } from './__generated__/Bar_build.graphql';
import { shortMessage, shortCommit } from 'app/lib/commits';

const PASSED_COLOR = "build-passed";
const PASSED_COLOR_HOVER = "build-passed-dark";
const RUNNING_COLOR = "build-running";
const RUNNING_COLOR_HOVER = "build-running-dark";
const FAILED_COLOR = "build-failed";
const FAILED_COLOR_HOVER = "build-failed-dark";
const SCHEDULED_COLOR = "build-scheduled";
const SCHEDULED_COLOR_HOVER = "build-scheduled-dark";
const PENDING_COLOR = "build-pending";
const PENDING_COLOR_HOVER = "build-pending-dark";
const SKIPPED_COLOR = "build-skipped";
const SKIPPED_COLOR_HOVER = "build-skipped-dark";
const NOT_RUN_COLOR = SKIPPED_COLOR;
const NOT_RUN_COLOR_HOVER = SKIPPED_COLOR_HOVER;

type Props = {
  href?: string,
  duration: number,
  graph: {
    maximumDuration: number
  },
  left: number,
  build: ?Bar_build,
  showFullGraph: boolean
};

type State = {
  hover: boolean
};

class Bar extends React.Component<Props, State> {
  static defaultProps = {
    showFullGraph: true
  };

  state = {
    hover: false
  }

  handleMouseOver = () => {
    this.setState({ hover: true });
  }

  handleMouseOut = () => {
    this.setState({ hover: false });
  }

  colorForBuild(build) {
    if (!build) {
      return PENDING_COLOR;
    }

    switch (build.state) {
      case BuildStates.SCHEDULED: return SCHEDULED_COLOR;
      case BuildStates.RUNNING: return RUNNING_COLOR;
      case BuildStates.PASSED: return PASSED_COLOR;
      case BuildStates.BLOCKED:
        switch (build.blockedState) {
          case BuildStates.FAILED:
            return FAILED_COLOR;
          case BuildStates.RUNNING:
            return RUNNING_COLOR;
          case BuildStates.PASSED:
          default:
            return PASSED_COLOR;
        }
      case BuildStates.SKIPPED: return SKIPPED_COLOR;
      case BuildStates.NOT_RUN: return NOT_RUN_COLOR;
      default: return FAILED_COLOR;
    }
  }

  hoverColorForBuild(build) {
    if (!build) {
      return PENDING_COLOR_HOVER;
    }

    switch (build.state) {
      case BuildStates.SCHEDULED: return SCHEDULED_COLOR_HOVER;
      case BuildStates.RUNNING: return RUNNING_COLOR_HOVER;
      case BuildStates.PASSED: return PASSED_COLOR_HOVER;
      case BuildStates.BLOCKED:
        switch (build.blockedState) {
          case BuildStates.FAILED:
            return FAILED_COLOR_HOVER;
          case BuildStates.RUNNING:
            return RUNNING_COLOR_HOVER;
          case BuildStates.PASSED:
          default:
            return PASSED_COLOR_HOVER;
        }
      case BuildStates.SKIPPED: return SKIPPED_COLOR_HOVER;
      case BuildStates.NOT_RUN: return NOT_RUN_COLOR_HOVER;
      default: return FAILED_COLOR_HOVER;
    }
  }

  calculateSizes() {
    // Calcualte what percentage this bar is in relation to the longest
    // running build
    let height = (this.props.duration / this.props.graph.maximumDuration);
    let transform = 'none';

    // See if the height is less than our minimum. If it is, set a hard pixel
    // height, otherwise make the height a percentage. We use percentages so
    // we can animate the height of the graph as it loads in.
    const heightInPixels = (height * GRAPH_HEIGHT);
    if (heightInPixels < BAR_HEIGHT_MINIMUM) {
      height = BAR_HEIGHT_MINIMUM;
    } else {
      height = `${height * 100}%`;
      if (!this.props.showFullGraph) {
        transform = `scaleY(${BAR_HEIGHT_MINIMUM / heightInPixels})`;
      }
    }

    return { height, transform };
  }

  render() {
    const { height, transform } = this.calculateSizes();
    const stateColorName = (
      this.state.hover
        ? this.hoverColorForBuild(this.props.build)
        : this.colorForBuild(this.props.build)
    );

    if (this.props.href) {
      return (
        <AnchoredPopover
          position="absolute"
          style={{ height: '100%', left: this.props.left, bottom: 0 }}
          width={300}
        >
          <a
            href={this.props.href}
            className="border-box inline-block color-inherit"
            style={{ height: '100%', width: BAR_WIDTH_WITH_SEPERATOR }}
            onMouseOver={this.handleMouseOver}
            onMouseOut={this.handleMouseOut}
          >
            <div
              className={classNames(
                "border-box inline-block absolute",
                `bg-${stateColorName}`,
                {
                  'animation-running-bar': this.props.build && this.props.build.state === BuildStates.RUNNING
                }
              )}
              style={{
                height,
                width: BAR_WIDTH,
                left: 0,
                bottom: 0,
                transform,
                transformOrigin: 'bottom',
                transition: 'transform 200ms ease-in-out',
                backgroundSize: '.5rem .5rem',
                backgroundImage: (
                  this.props.build && this.props.build.state === BuildStates.RUNNING
                    ? 'linear-gradient(45deg, rgba(255,255,255,.5) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.5) 50%, rgba(255,255,255,.5) 75%, transparent 75%, transparent)'
                    : 'none'
                )
              }}
            />
            <span className="offscreen">{this.a11yText()}</span>
          </a>
          <BuildTooltip
            build={this.props.build}
            visible={this.state.hover}
          />
        </AnchoredPopover>
      );
    }

    return (
      <div
        className={classNames(
          "border-box inline-block absolute border",
          `border-${stateColorName}`
        )}
        style={{
          height: BAR_HEIGHT_MINIMUM,
          left: this.props.left,
          width: BAR_WIDTH,
          bottom: 0
        }}
      />
    );
  }

  a11yText() {
    if (this.props.build && this.props.build.message) {
      return `${shortMessage(this.props.build.message)} ${this.branchOrCommit()}`;
    }

    return "";
  }

  branchOrCommit() {
    if (!this.props.build) {
      return "";
    }

    if (this.props.build.pipeline.defaultBranch) {
      return shortCommit(
        this.props.build.commit,
        (
          this.props.build.pipeline &&
          this.props.build.pipeline.commitShortLength
        ) ||
        undefined
      );
    }
    return this.props.build.branch;
  }
}

export default createRefetchContainer(Bar, graphql`
  fragment Bar_build on Build {
      message
      branch
      commit
      state
      blockedState
      pipeline {
        commitShortLength
        defaultBranch
      }
      ...BuildTooltip_build
    }
`);
